import { FC } from 'react'
import styled, { css } from 'styled-components'
import { Small } from '@/components/atoms/typography'
import {
  display,
  minWidth,
  width,
  space,
  margin,
  padding,
  flexGrow
} from 'styled-system'
import { lighten } from 'polished'
import { Flex } from '@/components/atoms/Grid'

type ToggleButtonProps = {
  selected: boolean
  underline?: boolean
  isDisabled?: boolean
  title: string
  onClick: () => void
} & Rest

type IconToggleButtonProps = {
  iconElement: JSX.Element
} & ToggleButtonProps

export const ToggleButton: FC<ToggleButtonProps> = ({
  title,
  onClick,
  isDisabled,
  selected,
  underline = true,
  ...rest
}) => (
  <StyledToggleButton
    onClick={onClick}
    selected={selected}
    isDisabled={isDisabled}
    underline={underline}
    {...rest}
  >
    <Small my={15} mx={3}>
      {title}
    </Small>
  </StyledToggleButton>
)

export const IconToggleButton: FC<IconToggleButtonProps> = ({
  title,
  onClick,
  isDisabled,
  selected,
  iconElement,
  underline = true,
  ...rest
}) => (
  <StyledToggleButton
    onClick={onClick}
    selected={selected}
    isDisabled={isDisabled}
    underline={underline}
    {...rest}
  >
    <Flex
      my={15}
      mx={3}
      flexDirection={{ desktop: 'column', tablet: 'column', mobile: 'row' }}
      alignItems="center"
    >
      {iconElement}
      <Small
        pt={{ desktop: 2, tablet: 2, mobile: 0 }}
        pl={{ desktop: 0, tablet: 0, mobile: 2 }}
        my={0}
      >
        {title}
      </Small>
    </Flex>
  </StyledToggleButton>
)

const ToggleButtonStyleProps = css`
  ${space};
  ${width};
  ${display};
  ${minWidth};
  ${margin};
  ${padding};
  ${flexGrow};
`

const StyledToggleButton = styled.button`
  ${ToggleButtonStyleProps}
  border: 0;
  border-radius: ${(props) => props.theme.buttonBorderRadius}px;
  background: none;
  background-color: ${(props) =>
    props.selected
      ? lighten(0.3, props.theme.colors.accent)
      : props.theme.colors.background};
  text-decoration: ${(props) =>
    props.selected || !props.underline ? 'none' : 'underline'};
  opacity: ${(props) => (props.isDisabled ? 0.6 : 1)};
  text-transform: uppercase;
  &:hover {
    background-color: ${(props) => lighten(0.3, props.theme.colors.accent)};
    text-decoration: none;
    cursor: pointer;
  }
`
